// Persistent best-score store backed by localStorage.
//
// One JSON blob under STORAGE_KEY holds a record per mode key. Mode keys
// are namespaced strings:
//   story:1, story:2, story:3, story:4
//   endless
//   daily:YYYY-MM-DD (UTC)
//
// Submitting a result returns whether it was a new best (strictly greater
// score) so the game layer can trigger NEW BEST celebration UX.

const STORAGE_KEY = "morph.best.v1";

export interface BestRecord {
  score: number;
  distance: number;
  combo: number;
  ts: number;
}

export interface SubmitResult {
  isNewBest: boolean;
  previous: BestRecord | null;
}

type Store = Record<string, BestRecord>;

export class BestScoresService {
  private cache: Store | null = null;

  get(modeKey: string): BestRecord | null {
    return this.load()[modeKey] ?? null;
  }

  submit(
    modeKey: string,
    record: Omit<BestRecord, "ts">
  ): SubmitResult {
    const store = this.load();
    const previous = store[modeKey] ?? null;
    const isNewBest = !previous || record.score > previous.score;
    if (isNewBest) {
      store[modeKey] = { ...record, ts: Date.now() };
      this.persist(store);
    }
    return { isNewBest, previous };
  }

  // For tests / future "wipe records" UX.
  clear(): void {
    this.cache = {};
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore quota / privacy errors
    }
  }

  private load(): Store {
    if (this.cache) return this.cache;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        this.cache = {};
        return this.cache;
      }
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object") {
        this.cache = parsed as Store;
        return this.cache;
      }
    } catch {
      // Storage may be unavailable (private mode, disabled). Fall through
      // to an empty in-memory store; we just lose persistence.
    }
    this.cache = {};
    return this.cache;
  }

  private persist(store: Store): void {
    this.cache = store;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
      // ignore
    }
  }
}
